import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import BookingForm from '../components/BookingForm';
import SeoHead from '../components/SeoHead';
import { DRIVER_BATA, MIN_ONEWAY_KM } from '../data/bookingConfig';
import '../styles/RouteLanding.css';

const BOOKING_ID = 'district-booking';

const scrollToBooking = () => {
  const target = document.getElementById(BOOKING_ID);
  if (target) {
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

const DistrictLandingPage = ({ page }) => {
  const [selectedDrop, setSelectedDrop] = useState('');

  const jsonLd = useMemo(() => ([
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://easydroptaxi.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: page.title,
          item: `https://easydroptaxi.com${page.slug}`,
        },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: page.title,
      serviceType: 'One-way taxi booking',
      provider: {
        '@type': 'TaxiService',
        name: 'Easy Drop Taxi',
        url: 'https://easydroptaxi.com/',
        telephone: '+919629954544',
      },
      areaServed: [page.name, 'Tamil Nadu'],
      url: `https://easydroptaxi.com${page.slug}`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: page.faqs.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    },
  ]), [page]);

  return (
    <main className="route-landing">
      <SeoHead
        title={page.seoTitle}
        description={page.seoDescription}
        path={page.slug}
        jsonLd={jsonLd}
      />

      <section className="rl-hero">
        <div className="rl-hero-inner">
          <div>
            <nav className="rl-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span aria-hidden="true">/</span>
              <span>{page.title}</span>
            </nav>
            <h1>{page.h1}</h1>
            <p className="rl-hero-lead">{page.heroLead}</p>
            <div className="rl-hero-note">
              <span><i className="fa fa-road" aria-hidden="true"></i>One Way Taxi</span>
              <span><i className="fa fa-phone" aria-hidden="true"></i><a href="tel:+919629954544">+91 96299 54544</a></span>
            </div>
          </div>

          <div id={BOOKING_ID} className="rl-booking-card">
            <BookingForm
              key={`${page.slug}-${selectedDrop}`}
              initialPickup={page.pickupName}
              initialDrop={selectedDrop}
              initialTripType="One Way"
              searchButtonLabel="Get Fare Estimate"
            />
          </div>
        </div>
      </section>

      <section className="rl-strip" aria-label="District taxi information">
        <div className="container">
          <div className="rl-strip-grid">
            <div className="rl-strip-item">
              <span className="rl-strip-label">District</span>
              <span className="rl-strip-value">{page.name}</span>
            </div>
            <div className="rl-strip-item">
              <span className="rl-strip-label">Trip type</span>
              <span className="rl-strip-value">One Way Taxi</span>
            </div>
            <div className="rl-strip-item">
              <span className="rl-strip-label">Coverage</span>
              <span className="rl-strip-value">District pickup</span>
            </div>
            <div className="rl-strip-item">
              <span className="rl-strip-label">Support</span>
              <span className="rl-strip-value">+91 96299 54544</span>
            </div>
          </div>
        </div>
      </section>

      <section className="rl-section">
        <div className="container">
          <h2>{page.title} Booking</h2>
          <div className="rl-prose">
            {page.intro.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
            <p>{page.placesNote}</p>
          </div>
        </div>
      </section>

      <section className="rl-section rl-section-muted">
        <div className="container">
          <h2>Drops from {page.name}</h2>
          <p className="rl-section-intro">
            These buttons only fill the booking card on this page. They do not open a new route URL.
          </p>
          <div className="rl-place-grid">
            {page.sampleDrops.map((drop) => (
              <button
                key={drop}
                type="button"
                className="rl-select-btn"
                onClick={() => {
                  setSelectedDrop(drop);
                  scrollToBooking();
                }}
              >
                Book drop to {drop}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="rl-section">
        <div className="container">
          <h2>Why Book from {page.name}</h2>
          <div className="rl-benefit-grid">
            {page.benefits.map((item) => (
              <article key={item.title} className="rl-benefit-card">
                <i className="fa fa-check" aria-hidden="true"></i>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="rl-section rl-section-muted">
        <div className="container">
          <h2>Pickup in {page.name}</h2>
          <div className="rl-prose">
            <p>{page.serviceNote}</p>
            <p>
              One-way billing uses a {MIN_ONEWAY_KM} km minimum and ₹{DRIVER_BATA} driver bata, matching every other Easy Drop Taxi estimate. Toll, state permit, parking and waiting time stay extra as shown at review.
            </p>
          </div>
        </div>
      </section>

      <section className="rl-section">
        <div className="container">
          <h2>How to Book</h2>
          <div className="rl-step-grid">
            {page.howTo.map((text, index) => (
              <article key={text} className="rl-step-card">
                <span className="rl-step-number">{index + 1}</span>
                <h3>{['Enter trip details', 'Select vehicle and time', 'Confirm the booking'][index]}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="rl-section rl-section-muted">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <div className="rl-faq">
            {page.faqs.map((item) => (
              <details key={item.question}>
                <summary>{item.question}</summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="rl-section">
        <div className="container">
          <h2>Related Pages</h2>
          <p className="rl-section-intro">
            Links below open pages that already exist, or another district page from this same set. No extra route slug is created from this list.
          </p>
          <div className="rl-links">
            {page.related.map((link) => (
              <Link key={link.path} to={link.path}>{link.label}</Link>
            ))}
          </div>
        </div>
      </section>

      <section className="rl-cta">
        <div className="container">
          <h2>{page.ctaTitle}</h2>
          <p>{page.ctaBody}</p>
          <button type="button" className="rl-cta-btn" onClick={scrollToBooking}>
            Book Your Taxi
          </button>
        </div>
      </section>
    </main>
  );
};

export default DistrictLandingPage;
